/* ============================================================================
   D1 sync layer.
   The reactive stores stay the source of truth for rendering; this module
   mirrors them to the Worker's D1 database so every party shares live data:

   - On start: pull /api/state. Server rows win; an empty server gets
     bootstrapped with the local seeds. No DB bound → stay "local".
   - On every store mutation: push that aggregate (queued, retried).
   - Poll (and on tab focus): pull rows whose updated_at moved, skipping
     anything we still have queued locally. Last write wins per aggregate.
   ========================================================================== */
import { useEffect, useState } from "react";
import { getTenders, onTenderChange, replaceTenders } from "./store";
import { getProjects, onProjectChange, onProjectDelete, replaceProjects, projectsReady } from "./pmStore";
import { getAccessAggregates, onAccessChange, replaceAccessAggregates, AccessAggregate } from "./accessStore";
import { getSettingsAggregates, replaceSettingsAggregates } from "./settingsStore";
import { authHeader } from "./managerAuth";
import { getStudioTeamAggregates, onStudioTeamChange, replaceStudioTeamAggregates, StudioTeamAggregate } from "./studioTeam";
import { getStudioBrandAggregates, onStudioBrandChange, replaceStudioBrandAggregates, StudioBrandAggregate } from "./studioBrand";
import { getPeopleAggregates, onPeopleChange, replacePeopleAggregates, PeopleAggregate } from "./people";
import { getDirectoryAggregates, onDirectoryChange, replaceDirectoryAggregates, DirectoryAggregate } from "./directoryStore";
import { getPhotoAggregates, onPhotoChange, onPhotoDelete, replacePhotoAggregates, resyncMissingPhotos, notePhotoStamps, demandedProjects, onPhotoDemand, photoStoreReady, hasPhotoLocally, dropPhotosLocally, requestedPhotoIds, isMobilePhotoMode } from "./photoStore";
import { getSheetAggregates, onSheetChange, replaceSheetAggregates, SheetImage, sheetsReady, dropSheetsLocally } from "./sheetStore";
import type { Tender, Project, SitePhoto } from "./types";
import { canon, diffProject, isRecordList, partHashes, projectHash, quickHash } from "./canon";

export type SyncStatus = "local" | "syncing" | "live" | "error" | "offline" | "signedout";

let status: SyncStatus = "local";
const statusSubs = new Set<() => void>();
function setStatus(s: SyncStatus) {
  if (status === s) return;
  status = s;
  statusSubs.forEach((f) => f());
}
export function getSyncStatus() { return status; }
export function useSyncStatus(): SyncStatus {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    statusSubs.add(f);
    return () => { statusSubs.delete(f); };
  }, []);
  return status;
}

type Kind = "tender" | "project" | "meta" | "photo" | "sheet";
type Row = { id: string; json: string; updated_at: number; slim?: boolean };

/* ---- record-level project sync (client half; the server half and the
   reasoning are in src/server/index.ts and src/lib/canon.ts) ---------------
   `baselines` holds, per project, a fingerprint of each record and field as
   the SERVER last had them. A push sends only what differs from that; a pull
   asks only for what moved since our version. Both finish with a whole-
   project fingerprint check, and anything that does not match falls back to
   the complete copy. */
const baselines = new Map<string, Map<string, string>>();
const canHash = typeof crypto !== "undefined" && !!crypto.subtle && typeof TextEncoder !== "undefined";
const yieldToUi = () => new Promise<void>((r) => setTimeout(r, 0));

const stamps = new Map<string, number>();                  // "kind:id" → server updated_at
const dirty = new Map<string, { kind: Kind; id: string; json: string }>();
const tombstones = new Set<string>();                      // deleted "kind:id" — never re-merge
/* Rows the server PERMANENTLY refused (a photo over the size cap, say). Kept
   out of the outbox so one poison row can't wedge every push behind it — the
   old flush() threw on the first non-ok and restarted from the front, so a
   single oversized photo froze a contractor's whole gallery indefinitely. */
const quarantine = new Map<string, { kind: Kind; id: string; status: number }>();
export function quarantinedCount(): number { return quarantine.size; }
let pushing = false;
let pulling = false;
let enabled = false;
const POLL_MS = 15_000;

function key(kind: Kind, id: string) { return `${kind}:${id}`; }

/* Every request has a deadline. On site 3G a stalled connection used to leave
   `pushing` set for good, so nothing more went up until the page was reloaded
   and the badge sat on "syncing" all day. */
async function fetchT(url: string, init: RequestInit = {}, ms = 30_000): Promise<Response> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), ms);
  try { return await fetch(url, { ...init, signal: ctl.signal }); }
  finally { clearTimeout(timer); }
}

/* Edits are gathered for a moment before they are serialised and queued:
   a typed note or a slider used to re-stringify an 800 KB project, rewrite
   the outbox and start a full upload on every keystroke. A pending edit
   protects its row from incoming merges exactly like a queued one. */
const pendingObj = new Map<string, { kind: Kind; id: string; obj: { id: string } }>();
let pendingTimer: ReturnType<typeof setTimeout> | null = null;
function isLocal(k: string) { return dirty.has(k) || pendingObj.has(k); }
function settlePending() {
  if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
  if (!pendingObj.size) return;
  for (const [k, p] of pendingObj) dirty.set(k, { kind: p.kind, id: p.id, json: JSON.stringify(p.obj) });
  pendingObj.clear();
  persistOutbox();
}
if (typeof window !== "undefined") {
  // leaving the page — what was typed in the last instant must not be lost
  window.addEventListener("pagehide", settlePending);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") settlePending(); });
}

/* ---- the outbox --------------------------------------------------------- */
/* A queue that only lives in memory is a queue that loses a daily report the
   moment somebody closes the tab in a dead spot. Everything waiting to go up
   is mirrored to localStorage and reloaded on the next start, so work done
   without signal survives a refresh, a crash and a flat battery. */

const OUTBOX_KEY = "atelier.outbox.v1";
/** Heavy rows (photos, sheet renders) are already big; cap what we persist so
    a long offline stretch can't blow the storage quota and lose everything. */
const OUTBOX_MAX_BYTES = 3_500_000;

function persistOutbox() {
  try {
    const rows: { kind: Kind; id: string; json: string }[] = [];
    let bytes = 0;
    for (const item of dirty.values()) {
      bytes += item.json.length;
      if (bytes > OUTBOX_MAX_BYTES) break;
      rows.push(item);
    }
    if (rows.length) localStorage.setItem(OUTBOX_KEY, JSON.stringify(rows));
    else localStorage.removeItem(OUTBOX_KEY);
  } catch { /* quota — the in-memory queue still holds it for this session */ }
}

let restored = false;
function restoreOutbox() {
  if (restored) return;      // start() retries on a timer; only read the disk once
  restored = true;
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) return;
    const rows = JSON.parse(raw) as { kind: Kind; id: string; json: string }[];
    for (const r of rows) {
      if (!r?.kind || !r?.id || typeof r.json !== "string") continue;
      // anything changed in THIS session is newer than what was on disk
      const k = key(r.kind, r.id);
      if (!dirty.has(k) && !tombstones.has(k)) dirty.set(k, r);
    }
    if (dirty.size) console.log(`outbox: restored ${dirty.size} change(s) captured offline`);
  } catch { /* unreadable — start clean rather than block the app */ }
}

/* Deletions wait in their own queue — a fire-and-forget DELETE that lost
   its one retry left the row on the server, and the photo the manager had
   just removed was back on every other device (and on their own after a
   reload, since the in-memory tombstone died with the tab). */
const DELBOX_KEY = "atelier.outbox.del.v1";
const pendingDeletes = new Map<string, { kind: Kind; id: string }>();
function persistDeletes() {
  try {
    if (pendingDeletes.size) localStorage.setItem(DELBOX_KEY, JSON.stringify([...pendingDeletes.values()]));
    else localStorage.removeItem(DELBOX_KEY);
  } catch { /* quota — in-memory still holds it */ }
}
function restoreDeletes() {
  try {
    const raw = localStorage.getItem(DELBOX_KEY);
    if (!raw) return;
    for (const d of JSON.parse(raw) as { kind: Kind; id: string }[]) {
      if (!d?.kind || !d?.id) continue;
      const k = key(d.kind, d.id);
      pendingDeletes.set(k, d);
      tombstones.add(k);
      dirty.delete(k);
    }
  } catch { /* unreadable — start clean */ }
}

/** How much work is waiting to reach the server. */
export function pendingCount(): number { return dirty.size + pendingObj.size + pendingDeletes.size; }

/** The browser's own view, which is a floor not a guarantee: online can still
    mean a captive portal or a dead uplink, which is why a failed flush also
    counts as offline. */
export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** Delete the row server-side and tombstone it so an in-flight poll can't
    bring it back. Queued and persisted like any other write, so it survives
    a dead spot, a closed tab and a reload. */
function pushDelete(kind: Kind, id: string) {
  const k = key(kind, id);
  tombstones.add(k);
  dirty.delete(k);
  pendingObj.delete(k);
  stamps.delete(k);
  pendingDeletes.set(k, { kind, id });
  persistOutbox();
  persistDeletes();
  if (status !== "local") setStatus(isOffline() ? "offline" : "syncing");
  void flush();
}

function queuePush(kind: Kind, aggregate: { id: string }) {
  // Queue even before the initial pull finishes — being pending protects a
  // pre-pull mutation from the first merge, and flush() drains it once live.
  // Photos and sheets are whole new rows, not typing — queue them at once.
  const k = key(kind, aggregate.id);
  if (kind === "photo" || kind === "sheet") {
    pendingObj.delete(k);
    dirty.set(k, { kind, id: aggregate.id, json: JSON.stringify(aggregate) });
    persistOutbox();
  } else {
    pendingObj.set(k, { kind, id: aggregate.id, obj: aggregate });
    if (!pendingTimer) pendingTimer = setTimeout(() => { pendingTimer = null; settlePending(); void flush(); }, 700);
  }
  if (status !== "local" && status !== "signedout") setStatus(isOffline() ? "offline" : "syncing");
  if (kind === "photo" || kind === "sheet") void flush();
}

/* A 4xx the server will give every time — the row is malformed or too big, so
   retrying it forever only blocks everything behind it. 401 (auth) and 429
   (rate) are NOT permanent: pause and retry those. */
function isPermanentReject(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401 && status !== 429;
}

let needPull = false;
async function flush() {
  if (pushing || !enabled) return;
  settlePending();
  if (isOffline()) { setStatus("offline"); return; }   // don't burn retries with no uplink
  pushing = true;
  let hadTransient = false;
  try {
    // deletions first — a removal must never lose a race with a stale edit
    for (const [k, d] of [...pendingDeletes.entries()]) {
      let res: Response;
      try { res = await fetchT(`/api/aggregate/${d.kind}/${d.id}`, { method: "DELETE", headers: authHeader() }); }
      catch { hadTransient = true; break; }
      if (res.ok || res.status === 404 || res.status === 410 || isPermanentReject(res.status)) {
        pendingDeletes.delete(k);
        persistDeletes();
        continue;
      }
      hadTransient = true;   // 401 / 429 / 5xx — keep it queued
      break;
    }
    // iterate a snapshot: a row that fails is skipped, not left at the front to
    // wedge the queue, and rows added mid-flush are picked up next pass
    for (const [k, item] of [...dirty.entries()]) {
      if (!dirty.has(k)) continue;   // cleared by a newer write mid-flush
      // a project goes up as just its changed records when we know what the
      // server holds; otherwise whole, as before
      let fullObj: Record<string, unknown> | null = null;
      let fullHash: string | undefined;
      let deltaBody: string | null = null;
      if (item.kind === "project" && canHash) {
        try { fullObj = JSON.parse(item.json) as Record<string, unknown>; fullHash = await projectHash(fullObj); } catch { fullObj = null; fullHash = undefined; }
        const base = baselines.get(item.id);
        if (fullObj && base) {
          const d = diffProject(fullObj, base);
          if (d.size === 0) {
            // nothing differs from what the server holds — no request at all
            if (dirty.get(k) === item) dirty.delete(k);
            persistOutbox();
            continue;
          }
          deltaBody = JSON.stringify({ json: JSON.stringify(d.patch), delta: true, unset: d.unset, hash: fullHash });
        }
      }
      const put = (body: string) => fetchT(`/api/aggregate/${item.kind}/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body,
      }, body.length > 2_000_000 ? 120_000 : 45_000);
      let res: Response;
      try {
        res = await put(deltaBody ?? JSON.stringify({ json: item.json, ...(fullHash ? { hash: fullHash } : {}) }));
        // the server could not apply the change-only copy — send it whole
        if (deltaBody && res.status === 409) {
          baselines.delete(item.id);
          res = await put(JSON.stringify({ json: item.json, ...(fullHash ? { hash: fullHash } : {}) }));
        }
      } catch {
        // network fault — transient; stop here and let the poll/online retry
        hadTransient = true;
        break;
      }
      if (res.ok) {
        // whatever else the server holds, it now has our version of every
        // record — the next change-only push is measured from here
        if (fullObj) baselines.set(item.id, partHashes(fullObj));
        const { updated_at, merged } = await res.json().catch(() => ({ updated_at: Date.now(), merged: false })) as { updated_at: number; merged?: boolean };
        /* The server folded in records another device had written. Our copy
           is now behind the stored one — record a stamp just short of it so
           the next pull brings the merged project down, instead of this
           device carrying on with (and re-pushing) its stale copy. */
        stamps.set(k, merged ? toStamp(updated_at) - 1 : toStamp(updated_at));
        if (merged) needPull = true;
        if (dirty.get(k) === item) dirty.delete(k);
        persistOutbox();
        saveStamps();
        continue;
      }
      if (res.status === 410) {
        // deleted on another device while this one still had edits — the
        // delete wins, and this copy goes too
        tombstones.add(k);
        if (dirty.get(k) === item) dirty.delete(k);
        persistOutbox();
        if (item.kind === "photo") dropPhotosLocally([item.id]);
        continue;
      }
      if (isPermanentReject(res.status)) {
        // the server will never accept this row — quarantine it and KEEP GOING
        // so the rest of the gallery still syncs. Surfaced via quarantinedCount.
        quarantine.set(k, { kind: item.kind, id: item.id, status: res.status });
        if (dirty.get(k) === item) dirty.delete(k);
        persistOutbox();
        console.warn(`sync: ${item.kind}/${item.id} rejected ${res.status} — quarantined, not blocking the queue`);
        continue;
      }
      // 401 / 429 / 5xx — transient; stop and retry the whole queue later
      hadTransient = true;
      break;
    }
    setStatus(hadTransient ? (isOffline() ? "offline" : "error") : (dirty.size || pendingObj.size ? "syncing" : "live"));
  } catch {
    setStatus(isOffline() ? "offline" : "error");
    hadTransient = true;
  } finally {
    pushing = false;
  }
  // an edit that arrived while this pass was running goes now, not in 15 s
  if (!hadTransient && dirty.size) void flush();
  else if (needPull) { needPull = false; void tickNow?.(); }
}
let tickNow: (() => Promise<void>) | null = null;

/** Stamps are epoch millis, but a writer that stored an ISO string would make
    every numeric comparison NaN and silently freeze the merge — the row would
    simply never arrive. Coerce here so one bad writer can't stop the sync. */
function toStamp(v: unknown): number {
  if (typeof v === "number" && isFinite(v)) return v;
  const n = typeof v === "string" ? +new Date(v) : NaN;
  // an unreadable stamp must still merge, so fall back to "now"
  return isFinite(n) ? n : Date.now();
}

/** What we believe we last saw for an aggregate. Anything that isn't a finite
    number reads as 0 so the server copy wins — a poisoned stamp must never be
    able to lock a row out permanently. */
function localStamp(k: string): number {
  const v = stamps.get(k);
  return typeof v === "number" && isFinite(v) ? v : 0;
}

function parseRows<T>(rows: Row[]): Map<string, { value: T; updated_at: number }> {
  const out = new Map<string, { value: T; updated_at: number }>();
  for (const r of rows) {
    try { out.set(r.id, { value: JSON.parse(r.json) as T, updated_at: toStamp(r.updated_at) }); } catch { /* skip bad row */ }
  }
  return out;
}

/** Merge server rows into local state. Server wins for anything newer than
    our recorded stamp, except aggregates with local changes still queued.
    With `prune`, rows the server previously had but no longer returns are
    dropped locally too — that's how a deletion reaches every device. */
function mergeKind<T extends { id: string }>(
  kind: Kind, rows: Row[], local: T[], replace: (next: T[]) => void, present?: Set<string>, incremental = false,
) {
  let changed = false;
  const map = new Map(local.map((x) => [x.id, x]));
  const fresh: T[] = [];
  for (const r of rows) {
    const k = key(kind, r.id);
    if (tombstones.has(k)) continue;   // deleted here — the server copy is on its way out
    if (isLocal(k)) continue;
    const updated_at = toStamp(r.updated_at);
    if (localStamp(k) >= updated_at) continue;   // compare first — parse only what moved
    let value: T;
    try { value = JSON.parse(r.json) as T; } catch { continue; }
    map.set(r.id, value);
    fresh.push(value);
    stamps.set(k, updated_at);
    changed = true;
  }
  /* `present` is every id the server holds for this kind. Anything here that
     the server no longer has was deleted elsewhere — unless it is still on its
     way up from this device. (Before, only rows with a remembered stamp were
     dropped, and project stamps were never remembered across a reload, so a
     deleted project stayed on every device for good.) */
  if (present) {
    for (const id of [...map.keys()]) {
      const k = key(kind, id);
      if (!present.has(id) && !isLocal(k) && !pendingDeletes.has(k)) {
        map.delete(id);
        stamps.delete(k);
        changed = true;
      }
    }
  }
  // photo and sheet stores merge what they are given — hand them only what moved,
  // not every resident thumbnail again (which rewrote the whole cache per batch)
  if (changed) replace(incremental ? fresh : [...map.values()]);
}

type StampRow = { id: string; updated_at: number; pid?: string };

/* ---- remembered stamps ------------------------------------------------
   The photo rows a device already holds are on disk (IndexedDB); the record
   of WHICH server versions they are was only ever in memory — so every boot
   re-downloaded the entire library (1,900 rows, ~340 MB) to learn nothing.
   Photo stamps are now persisted (~60 KB) and trusted only for rows actually
   present on this device. */
const STAMPS_KEY = "atelier.stamps.v1";
let stampsSaveTimer: ReturnType<typeof setTimeout> | null = null;
function saveStamps() {
  if (stampsSaveTimer) return;
  stampsSaveTimer = setTimeout(() => {
    stampsSaveTimer = null;
    try {
      const out: Record<string, number> = {};
      for (const [k, v] of stamps) if (k.startsWith("photo:")) out[k] = v;
      localStorage.setItem(STAMPS_KEY, JSON.stringify(out));
    } catch { /* quota — worst case we re-fetch next boot */ }
  }, 1500);
}
function restoreStamps() {
  try {
    const raw = localStorage.getItem(STAMPS_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw) as Record<string, number>;
    for (const [k, v] of Object.entries(saved)) {
      const id = k.slice("photo:".length);
      if (hasPhotoLocally(id) && typeof v === "number") stamps.set(k, v);
    }
  } catch { /* unreadable — start clean */ }
}
type StateData = { enabled: boolean; tenders: Row[]; projects: Row[]; meta?: Row[]; photoStamps?: StampRow[]; sheetStamps?: StampRow[]; deleted?: { kind: Kind; id: string }[]; index?: { kind: Kind; id: string; updated_at: number }[]; now?: number };

/** 401 = not signed in (yet). The loop treats it as "wait for a login", not
    as an outage — kickSync() fires the moment a session token lands. */
class AuthRequired extends Error {}

/* Someone is signed in on this device but the server does not know them: a
   token it no longer recognises, OR no token at all — a sign-in made while
   the server could not be reached falls back to the device's own copy and
   never gets one. Both used to read "Local only", and with no token every
   drawing, PDF and photo original is refused, so pictures showed as broken
   with nothing saying why. Either way the fix is the same: sign in again. */
function signedInLocally(): boolean {
  try { return !!localStorage.getItem("bidroom.session.v1"); } catch { return false; }
}
const authStatus = (): SyncStatus => (authHeader()["X-Session-Token"] || signedInLocally() ? "signedout" : "local");

/* Only what changed since the last pull comes down; the server's own clock
   marks the boundary (with a margin for writes landing mid-request). The first
   pull of a page is always complete. */
let lastServerNow = 0;
async function pull(full = false): Promise<StateData> {
  const since = full || !lastServerNow ? 0 : Math.max(0, lastServerNow - 15_000);
  // slim: projects arrive as stamps only and are fetched record by record
  const res = await fetchT(`/api/state?slim=1${since ? `&since=${since}` : ""}`, { cache: "no-store", headers: authHeader() }, 45_000);
  if (res.status === 401) throw new AuthRequired();
  if (!res.ok) throw new Error(`state ${res.status}`);
  const data = await res.json() as StateData;
  if (typeof data.now === "number") lastServerNow = data.now;
  return data;
}

/* Heavy kinds (photos, sheet previews) don't ride in /api/state — the stamps
   tell us which rows moved, and we fetch just those in small batches so no
   single response can blow the worker's limits again. */
const ROW_BATCH = 5;
/* A phone downloads the newest rows of an open project in the background and
   nothing older unless something asks for it by id — today's report and the
   gallery's first screens, not a 170 MB history. */
/* 30, not 150: rows that still embed their thumbnail run to ~780 KB, and
   pulling 150 of them right after sign-in took a test iPhone from 126 MB to
   over 400 MB — with Drawings on top, iOS killed the tab. The gallery asks
   for older rows by id as it scrolls. */
const MOBILE_PHOTO_PULL = 30;
async function pullLazyKind(kind: "photo" | "sheet", stampList: StampRow[], merge: (rows: Row[]) => void) {
  /* Photos are fetched only for projects this device has asked for — a
     contractor's phone never downloads another site's gallery, and the
     manager's phone only pays for the project open in front of them. */
  const demand = kind === "photo" ? demandedProjects() : null;
  const asked = kind === "photo" ? new Set(requestedPhotoIds()) : new Set<string>();
  // newest first — the shot taken this afternoon is the first to land
  const ordered = [...stampList].sort((a, b) => toStamp(b.updated_at) - toStamp(a.updated_at));
  // a phone's background slice: each project's newest N by server time
  const rank = new Map<string, number>();
  const inSlice = new Set<string>();
  if (kind === "photo" && isMobilePhotoMode()) {
    for (const s of ordered) {
      const n = rank.get(s.pid ?? "") ?? 0;
      rank.set(s.pid ?? "", n + 1);
      if (n < MOBILE_PHOTO_PULL) inSlice.add(s.id);
    }
  }
  const wanted = ordered
    .filter((s) => {
      const k = key(kind, s.id);
      if (tombstones.has(k) || isLocal(k) || localStamp(k) >= toStamp(s.updated_at)) return false;
      if (!demand) return true;
      if (asked.has(s.id)) return true;
      if (s.pid && !demand.has(s.pid)) return false;
      return inSlice.size === 0 && rank.size === 0 ? true : inSlice.has(s.id) || !s.pid;
    })
    .map((s) => s.id);
  for (let i = 0; i < wanted.length; i += ROW_BATCH) {
    const res = await fetchT(`/api/rows/${kind}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ ids: wanted.slice(i, i + ROW_BATCH) }),
      cache: "no-store",
    });
    if (!res.ok) throw new Error(`rows ${res.status}`);
    const { rows } = await res.json() as { rows: Row[] };
    if (rows?.length) merge(rows);
  }
}

/* The "meta" kind carries several small aggregates; each store picks out its
   own row(s) by id and ignores the rest. */
function replaceMetaRows(rows: { id: string }[]) {
  replaceAccessAggregates(rows as AccessAggregate[]);
  replaceSettingsAggregates(rows);
  replaceStudioTeamAggregates(rows);
  replaceStudioBrandAggregates(rows);
  replacePeopleAggregates(rows);
  replaceDirectoryAggregates(rows);
}

/** Fetch what moved in one project since the version this device holds:
    nothing (already the same), the changed records, or — when the change
    index does not reach back that far — the whole project. Returns the row
    to merge, or null when there is nothing to merge. */
async function fetchProject(id: string, since: number, allowDelta = true): Promise<Row | null> {
  const k = key("project", id);
  const local = allowDelta ? (getProjects().find((p) => p.id === id) as unknown as Record<string, unknown> | undefined) : undefined;
  const have = local && canHash ? await projectHash(local) : undefined;
  const scalars: Record<string, string> = {};
  if (local) for (const f of Object.keys(local)) {
    const v = local[f];
    if (v === undefined || isRecordList(v) || (Array.isArray(v) && v.length === 0)) continue;
    scalars[f] = quickHash(canon(v));
  }
  const res = await fetchT(`/api/project-delta/${encodeURIComponent(id)}`, {
    method: "POST", cache: "no-store",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ since: local ? since : 0, have, scalars: local ? scalars : {} }),
  }, 45_000);
  if (res.status === 401) throw new AuthRequired();
  if (res.status === 404 || res.status === 410) return null;   // gone — the index prunes it
  if (!res.ok) throw new Error(`project-delta ${res.status}`);
  type Delta = { kind: "same" | "full" | "delta"; updated_at: number; hash: string; json?: string; scalarKeys?: string[]; scalars?: Record<string, unknown>; changed?: Record<string, { id: string }[]>; removed?: Record<string, string[]> };
  const j = await res.json() as Delta;
  if (j.kind === "same") {
    stamps.set(k, toStamp(j.updated_at));
    if (local) baselines.set(id, partHashes(local));
    return null;
  }
  if (j.kind === "full" || !local) {
    const obj = JSON.parse(j.json ?? "null") as Record<string, unknown> | null;
    if (!obj) return null;
    if (canHash) baselines.set(id, partHashes(obj));
    return { id, json: j.json!, updated_at: j.updated_at };
  }
  // apply the changes to our copy
  const obj: Record<string, unknown> = {};
  for (const f of j.scalarKeys ?? []) obj[f] = f in (j.scalars ?? {}) ? j.scalars![f] : local[f];
  const plain = new Set(j.scalarKeys ?? []);
  const regs = new Set([...Object.keys(local).filter((f) => isRecordList(local[f])), ...Object.keys(j.changed ?? {})]);
  for (const f of regs) {
    if (plain.has(f)) continue;   // the server now holds this as a plain field
    const gone = new Set(j.removed?.[f] ?? []);
    const next = (isRecordList(local[f]) ? (local[f] as { id: string }[]) : []).filter((r) => !gone.has(r.id));
    const at = new Map(next.map((r, i) => [r.id, i] as const));
    for (const r of j.changed?.[f] ?? []) {
      const i = at.get(r.id);
      if (i === undefined) { at.set(r.id, next.length); next.push(r); } else next[i] = r;
    }
    if (next.length) obj[f] = next;
  }
  // the whole-project check: anything off and we take the complete copy
  if ((await projectHash(obj)) !== j.hash) {
    console.warn(`sync: ${id} change-only copy did not match — fetching it whole`);
    return fetchProject(id, 0, false);
  }
  baselines.set(id, partHashes(obj));
  return { id, json: JSON.stringify(obj), updated_at: j.updated_at };
}

/** Turn slim project rows (stamp only) into rows with their content, fetching
    only those this device does not already hold. */
async function resolveProjects(rows: Row[]): Promise<Row[]> {
  const out: Row[] = [];
  for (const r of rows) {
    if (!r.slim) {
      out.push(r);
      if (canHash) { try { baselines.set(r.id, partHashes(JSON.parse(r.json))); } catch { /* unreadable — sent whole next time */ } }
      continue;
    }
    const k = key("project", r.id);
    if (tombstones.has(k) || isLocal(k)) continue;    // our edits go up first; the merged copy follows
    if (localStamp(k) >= toStamp(r.updated_at)) continue;
    try {
      const got = await fetchProject(r.id, localStamp(k));
      if (got) out.push(got);
    } catch (e) {
      if (e instanceof AuthRequired) throw e;
      console.warn(`sync: project ${r.id} deferred`, e);   // retried on the next poll
    }
    await yieldToUi();   // fingerprints are CPU work — keep the page responsive between projects
  }
  return out;
}

async function mergeAll(data: StateData) {
  // the full id list decides deletions; an older server without it prunes nothing
  const present = (kind: Kind) => data.index ? new Set(data.index.filter((r) => r.kind === kind).map((r) => r.id)) : undefined;
  mergeKind("tender", data.tenders, getTenders(), replaceTenders, present("tender"));
  mergeKind("project", await resolveProjects(data.projects), getProjects(), replaceProjects, present("project"));

  mergeKind<{ id: string }>("meta", data.meta ?? [], [...getAccessAggregates(), ...getSettingsAggregates(), ...getStudioTeamAggregates(), ...getStudioBrandAggregates(), ...getPeopleAggregates(), ...getDirectoryAggregates()], replaceMetaRows);
  // rows deleted on any device: drop ours, and never offer them back up
  const gone: string[] = [];
  const goneSheets: string[] = [];
  for (const d of data.deleted ?? []) {
    const k = key(d.kind, d.id);
    if (!tombstones.has(k)) { tombstones.add(k); stamps.delete(k); if (dirty.delete(k)) persistOutbox(); }
    if (d.kind === "photo") gone.push(d.id);
    if (d.kind === "sheet") goneSheets.push(d.id);
  }
  if (gone.length) dropPhotosLocally(gone);
  if (goneSheets.length) dropSheetsLocally(goneSheets);
  notePhotoStamps(data.photoStamps ?? []);   // counts for projects we haven't opened
  // one bad photo or sheet batch must never stop projects from syncing
  try { await pullLazyKind("photo", data.photoStamps ?? [], (rows) => mergeKind<SitePhoto>("photo", rows, getPhotoAggregates(), replacePhotoAggregates, undefined, true)); }
  catch (e) { console.warn("sync: photo rows deferred", e); }
  saveStamps();
  try {
    await sheetsReady;   // the device's cached previews first, so only what is missing is fetched
    await pullLazyKind("sheet", data.sheetStamps ?? [], (rows) => mergeKind<SheetImage>("sheet", rows, getSheetAggregates(), replaceSheetAggregates, undefined, true));
  } catch (e) { console.warn("sync: sheet rows deferred", e); }

  // heal any photo the server never received — the backlog an oversized row
  // wedged, and any gap since. queuePush dedups, so this is a no-op once caught up.
  // A photo the server refused is not offered again every 15 seconds.
  const serverPhotoIds = new Set((data.photoStamps ?? []).map((s) => s.id));
  const deletedPhotoIds = new Set([...tombstones, ...quarantine.keys()].filter((k) => k.startsWith("photo:")).map((k) => k.slice("photo:".length)));
  void resyncMissingPhotos(serverPhotoIds, deletedPhotoIds);
}

async function bootstrap() {
  await fetch("/api/bootstrap", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({
      tenders: getTenders().map((t) => ({ id: t.id, json: JSON.stringify(t) })),
      projects: getProjects().map((p) => ({ id: p.id, json: JSON.stringify(p) })),
      photos: getPhotoAggregates().map((p) => ({ id: p.id, json: JSON.stringify(p) })),
    }),
  });
}

let started = false;
export function initSync() {
  if (started) return;
  started = true;

  onTenderChange((t: Tender) => queuePush("tender", t));
  onProjectChange((p: Project) => queuePush("project", p));
  onProjectDelete((pid: string) => pushDelete("project", pid));
  onAccessChange((a: AccessAggregate) => queuePush("meta", a));
  // NOTE: studio-settings deliberately has no push hook — the passphrase row
  // is server-managed via /api/auth and the worker rejects direct writes.
  onStudioTeamChange((a: StudioTeamAggregate) => queuePush("meta", a));
  onStudioBrandChange((a: StudioBrandAggregate) => queuePush("meta", a));
  onPeopleChange((a: PeopleAggregate) => queuePush("meta", a));
  onDirectoryChange((a: DirectoryAggregate) => queuePush("meta", a));
  onPhotoChange((p: SitePhoto) => queuePush("photo", p));
  onPhotoDelete((id: string) => pushDelete("photo", id));
  onSheetChange((s: SheetImage) => queuePush("sheet", s));

  let attached = false;
  let tickRef: (() => Promise<void>) | null = null;
  onPhotoDemand(() => { void tickRef?.(); });   // a project just opened → fetch its rows now
  const start = async (): Promise<boolean> => {
    try {
      await photoStoreReady;   // the light index — stamps are trusted only for rows on disk
      await projectsReady;     // what this device holds, before the server's copy is merged in
      restoreStamps();
      restoreOutbox();   // work captured offline before this session started
      restoreDeletes();
      let data = await pull(true);
      if (!data.enabled) { setStatus("local"); return true; } // no DB bound — final
      if (data.tenders.length === 0 && data.projects.length === 0) {
        await bootstrap();
        data = await pull(true);
      }
      await mergeAll(data);
      enabled = true;
      setStatus(dirty.size ? "syncing" : "live");
      void flush(); // drain anything queued while the pull was in flight

      const tick = async () => {
        if (pulling) return;   // a demand-triggered pass mid-timer-pass — let it finish
        // a tab nobody is looking at does not need to poll; it catches up on return
        if (typeof document !== "undefined" && document.visibilityState === "hidden" && !dirty.size && !pendingObj.size) return;
        pulling = true;
        try {
          if (dirty.size || pendingDeletes.size) { await flush(); }
          const d = await pull();
          if (d.enabled) {
            await mergeAll(d);
            if (!dirty.size) setStatus("live");
          }
        } catch (e) {
          // a token the server no longer knows (expired after 90 days, or minted
          // on a database since reset) must be SAID: a quiet "Local only" left a
          // MacBook on months-old data with no hint that it was no longer signed in
          setStatus(e instanceof AuthRequired ? authStatus() : "error");
        } finally { pulling = false; }
      };
      tickRef = tick;
      tickNow = tick;
      if (!attached) {
        attached = true;
        setInterval(tick, POLL_MS);
        window.addEventListener("focus", () => { void tick(); });
        document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void tick(); });

        // signal came back — drain the outbox at once rather than waiting out
        // the poll interval, which is a long time to stare at "offline"
        window.addEventListener("online", () => { setStatus("syncing"); void tick(); });
        window.addEventListener("offline", () => { if (dirty.size) setStatus("offline"); });
      }
      return true;
    } catch (e) {
      // unreachable or not signed in yet — keep trying below
      setStatus(e instanceof AuthRequired ? authStatus() : "local");
      return false;
    }
  };

  let retrying = false;
  const retry = async () => {
    if (retrying || enabled) return;
    retrying = true;
    try { await start(); } finally { retrying = false; }
  };
  kick = retry;

  void (async () => {
    if (await start()) return;
    // Not signed in yet, or a transient failure at load — retry on a timer,
    // on focus/online, and immediately after a successful login (kickSync).
    const timer = setInterval(() => { if (enabled) clearInterval(timer); else void retry(); }, 10_000);
    window.addEventListener("focus", () => { void retry(); });
    window.addEventListener("online", () => { void retry(); });
  })();
}

let kick: (() => Promise<void>) | null = null;
/** Fired right after a login so the first pull doesn't wait for the timer. */
export function kickSync() { void kick?.(); }
